import * as React from "react";
import { Text } from "@blueprintjs/core";
import styled from "styled-components";
import type { ComponentProps } from "widgets/BaseComponent";
import Interweave from "interweave";
import { UrlMatcher, EmailMatcher } from "interweave-autolink";
import type { TextSize } from "constants/WidgetConstants";
import { DEFAULT_FONT_SIZE, FontStyleTypes } from "constants/WidgetConstants";
import { Icon, IconSize } from "@design-system/widgets-old";
import { get } from "lodash";
import equal from "fast-deep-equal/es6";
import ModalComponent from "components/designSystems/appsmith/ModalComponent";
import type { Color } from "constants/Colors";
import { Colors } from "constants/Colors";
import { fontSizeUtility,subStringText } from "widgets/WidgetUtils";
import { OverflowTypes } from "../constants";
import LinkFilter from "./filters/LinkFilter";
import moment from "moment";
import { getIconPark } from "components/propertyControls/IconSelectControl";
import { formatNumber } from "@byk/utils/Utils";

export type TextAlign = "LEFT" | "CENTER" | "RIGHT" | "JUSTIFY";

const ELLIPSIS_HEIGHT = 15;

export const TextContainer = styled.div<{
  fontFamily?: string;
}>`
  & {
    height: 100%;
    width: 100%;
    position: relative;
  }
  ul {
    list-style-type: disc;
    list-style-position: inside;
  }
  ol {
    list-style-type: decimal;
    list-style-position: inside;
  }
  ul ul,
  ol ul {
    list-style-type: circle;
    list-style-position: inside;
    margin-left: 15px;
  }
  ol ol,
  ul ol {
    list-style-type: lower-latin;
    list-style-position: inside;
    margin-left: 15px;
  }
  h1 {
    font-size: 2em;
    margin: 0.67em 0;
  }
  h2 {
    font-size: 1.5em;
    margin: 0.75em 0;
  }
  h3 {
    font-size: 1.17em;
    margin: 0.83em 0;
  }
  h5 {
    font-size: 0.83em;
    margin: 1.5em 0;
  }
  h6 {
    font-size: 0.75em;
    margin: 1.67em 0;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: bold;
  }
  a {
    color: #106ba3;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  ${(props) =>
    props.fontFamily &&
    `
    font-family: ${props.fontFamily};
  `}
`;

const StyledIcon = styled(Icon)<{ backgroundColor?: string }>`
  cursor: pointer;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${ELLIPSIS_HEIGHT}px;
  background: ${(props) =>
    props.backgroundColor ? props.backgroundColor : "transparent"};
`;


type StyledTextProps = React.PropsWithChildren<{
  overflow: OverflowTypes;
  isTruncated: boolean;
  textAlign: string;
  backgroundColor?: string;
  textColor?: string;
  fontStyle?: string;
  fontSize?: TextSize;
  lineHeight?: string;
  borderRadius?: string;
  boxShadow?: string;
  borderWidth?: string;
  borderColor?: string;
}>;

export const StyledText = styled(Text)<StyledTextProps>`
  height: ${(props) =>
  props.overflow === OverflowTypes.TRUNCATE && props.isTruncated
    ? `calc(100% - ${ELLIPSIS_HEIGHT}px)`
    : "100%"};
  overflow-x: hidden;
  overflow-y: ${(props) =>
    props.overflow !== OverflowTypes.SCROLL ||
    props.overflow === OverflowTypes.TRUNCATE.valueOf()
      ? "hidden"
      : "auto"};
  text-overflow: ellipsis;
  && div {
    display: block;
    width: 100%;
  }
  display: flex;
  width: 100%;
  border-radius: ${({borderRadius}) => (borderRadius)};
  box-shadow: ${({ boxShadow }) => `${boxShadow ?? "none"}`} !important;
  border-width: ${({ borderWidth }) => `${borderWidth}px`};
  border-color: ${({ borderColor }) => borderColor} !important;
  justify-content: flex-start;
  flex-direction: ${(props) => (props.isTruncated ? "column" : "unset")};
  align-items: ${(props) =>
    props.overflow === OverflowTypes.SCROLL ||
    props.overflow === OverflowTypes.TRUNCATE
      ? "flex-start"
      : "center"};
  background: ${(props) => props?.backgroundColor};
  color: ${(props) => props?.textColor};
  font-style: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.ITALIC) ? "italic" : ""};
  text-decoration: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.UNDERLINE) ? "underline" : ""};
  font-weight: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.BOLD) ? "bold" : "normal"};
  font-size: ${({ fontSize }) =>
    fontSizeUtility(fontSize) || DEFAULT_FONT_SIZE};
  word-break: break-word;
  span {
    width: 100%;
    line-height: ${(props) => props?.lineHeight};
    white-space: pre-wrap;
    text-align: ${(props) => props.textAlign.toLowerCase()};
  }
  .auto-layout & span {
    min-height: 32px;
  }
`;

const ModalContent = styled.div<{
  backgroundColor?: string;
}>`
  background: ${(props) => props?.backgroundColor || Colors.WHITE};
  padding: 24px;
  padding-top: 16px;

  a {
    color: #106ba3;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Heading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  .title {
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: -0.24px;
    color: ${Colors.GREY_10};
  }
  .icon > svg > path {
    stroke: ${Colors.GREY_9};
  }
`;

const Content = styled.div<{
  fontSize?: TextSize;
  fontStyle?: string;
  textAlign: string;
  textColor?: string;
  lineHeight?:string;
}>`
  padding-top: 16px;
  color: ${(props) => props?.textColor};
  max-height: 70vh;
  overflow: auto;
  overflow-wrap: break-word;
  text-align: ${(props) => props.textAlign.toLowerCase()};
  font-style: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.ITALIC) ? "italic" : ""};
  text-decoration: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.UNDERLINE) ? "underline" : ""};
  font-weight: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.BOLD) ? "bold" : "normal"};
  font-size: ${({ fontSize }) =>
    fontSizeUtility(fontSize) || DEFAULT_FONT_SIZE};
`;
export interface TextComponentProps extends ComponentProps {
  accentColor: string;
  text?: string;
  length?: number;
  textAlign: TextAlign;
  ellipsize?: boolean;
  fontSize?: TextSize;
  lineHeight?: string;
  fontFamily: string;
  isLoading: boolean;
  backgroundColor?: string;
  textColor?: string;
  fontStyle?: string;
  disableLink: boolean;
  truncateButtonColor?: string;
  borderColor?: Color;
  borderWidth?: number;
  overflow: OverflowTypes;
  // helpers to detect and re-calculate content width
  bottomRow?: number;
  leftColumn?: number;
  rightColumn?: number;
  topRow?: number;
  onClick?: () => void;
  textType:string;
  dateFormat:string;
  dataFormat?:string;
  cancelBind:boolean;
  iconPosition?: string;
  iconColor?: string;
}

type State = {
  isTruncated: boolean;
  showModal: boolean;
};

type TextRef = React.Ref<Text> | undefined;

class TextComponent extends React.Component<TextComponentProps, State> {
  state = {
    isTruncated: false,
    showModal: false,
  };

  textRef = React.createRef() as TextRef;

  getTruncate = (element: any) => {
    const { isTruncated } = this.state;
    // add ELLIPSIS_HEIGHT and check content content is overflowing or not
    return (
      element.scrollHeight >
      element.offsetHeight + (isTruncated ? ELLIPSIS_HEIGHT : 0)
    );
  };

  componentDidMount = () => {
    const textRef = get(this.textRef, "current.textRef");
    if (textRef && this.props.overflow === OverflowTypes.TRUNCATE) {
      const isTruncated = this.getTruncate(textRef);
      this.setState({ isTruncated });
    }
  };

  componentDidUpdate = (prevProps: TextComponentProps) => {
    if (!equal(prevProps, this.props)) {
      if (this.props.overflow === OverflowTypes.TRUNCATE) {
        const textRef = get(this.textRef, "current.textRef");
        if (textRef) {
          const isTruncated = this.getTruncate(textRef);
          this.setState({ isTruncated });
        }
      } else if (
        prevProps.overflow === OverflowTypes.TRUNCATE &&
        this.props.overflow !== OverflowTypes.TRUNCATE.valueOf()
      ) {
        this.setState({ isTruncated: false });
      }
    }
  };

  handleModelOpen = () => {
    this.setState({ showModal: true });
  };

  handleModelClose = () => {
    this.setState({ showModal: false });
  };

  render() {
    const {
      accentColor,
      backgroundColor,
      disableLink,
      ellipsize,
      fontFamily,
      fontSize,
      fontStyle,
      overflow,
      text,
      length,
      textAlign,
      textColor,
      truncateButtonColor,
      lineHeight,
      cancelBind,
      textType,
      dateFormat,
      onClick,
      dataFormat,
      isDataFormat,
      iconPosition,
      iconColor,
      decimal,
    } = this.props;

    let textFormat:any = text;  // 1970-01-01 08:00:00
    if(isDataFormat){
      textFormat = dataFormat;
    }
    if (textType == 'DATE' && dateFormat && textFormat && parseInt(textFormat) ) {
      textFormat = moment(textFormat*1).format(dateFormat);
      if ('Invalid date' == textFormat) {
          textFormat = moment('1970-01-01 08:00:00').format(dateFormat);
      }
    }

    if (textType == 'NUMBER' ) {
      textFormat = formatNumber(textFormat,decimal);
    }
    const iconComponent = this.props.iconName&&<div style={{width:"30px" }} >
                          {getIconPark(this.props.iconName, this.props.iconColor)}
                        </div>;

    const InterweaveComponent =
      <Interweave
          content={subStringText(textFormat,length)}
          filters={[new LinkFilter()]}
          children={iconComponent}
          matchers={
            disableLink
              ? []
              : [new EmailMatcher("email"), new UrlMatcher("url")]
          }
          newWindow
        />
     ;

    return (
      <>
        <TextContainer
          fontFamily={fontFamily !== "System Default" ? fontFamily : undefined}
        >
          <StyledText
            backgroundColor={backgroundColor}
            className={this.props.isLoading ? "bp3-skeleton" : "bp3-ui-text"}
            ellipsize={ellipsize}
            fontSize={fontSize}
            lineHeight={lineHeight}
            fontStyle={fontStyle}
            isTruncated={this.state.isTruncated}
            overflow={overflow}
            ref={this.textRef}
            textAlign={textAlign}
            textColor={textColor}
            onClick={onClick}
            borderRadius={this.props.borderRadius}
            boxShadow={this.props.boxShadow}
            borderWidth={this.props.borderWidth}
            borderColor={this.props.borderColor}
          >{
            iconPosition=="right"?
            <>
              {InterweaveComponent}
              {iconComponent}
            </>:
            <>
              {iconComponent}
              {InterweaveComponent}
            </>
          }
            {/* {iconComponent}
            {InterweaveComponent} */}
            {/* {iconComponent} */}
          </StyledText>

          {this.state.isTruncated && (
            <StyledIcon
              backgroundColor={backgroundColor}
              className="t--widget-textwidget-truncate"
              fillColor={truncateButtonColor || accentColor}
              name="context-menu"
              onClick={this.handleModelOpen}
              size={IconSize.XXXL}
            />
          )}
        </TextContainer>

        <ModalComponent
          canEscapeKeyClose
          canOutsideClickClose
          className="t--widget-textwidget-truncate-modal"
          hasBackDrop
          isOpen={this.state.showModal}
          onClose={this.handleModelClose}
          overlayClassName="text-widget-truncate"
          scrollContents
          width={500}
        >
          <ModalContent backgroundColor={backgroundColor}>
            <Heading>
              <div className="title">Show More</div>
              <Icon
                className="icon"
                name="cross"
                onClick={this.handleModelClose}
                size={IconSize.MEDIUM}
              />
            </Heading>
            <Content
              fontSize={fontSize}
              fontStyle={fontStyle}
              textAlign={textAlign}
              textColor={textColor}
              lineHeight={lineHeight}
            >
              <Interweave
                content={textFormat}
                filters={[new LinkFilter()]}
                matchers={
                  disableLink
                    ? []
                    : [new EmailMatcher("email"), new UrlMatcher("url")]
                }
                newWindow
              />
            </Content>
          </ModalContent>
        </ModalComponent>
      </>
    );
  }
}

export default TextComponent;
